import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, PenLine } from 'lucide-react';
import api from '../../lib/api';
import { toSlug } from '../../lib/slug';
import type { Invitation } from '../../types';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardContent } from '../ui/Card';
import Textarea from '../ui/Textarea';
import Modal from '../ui/Modal';
import { AIAssistantShell, type AIAssistantField } from '../ai';

const scriptAIFields: AIAssistantField[] = [
  { key: 'mustHaveUsps', label: 'Must-have product USPs', type: 'textarea', placeholder: 'Key product features that MUST be mentioned...' },
  { key: 'keyTalkingPoints', label: 'Key talking points', type: 'textarea', placeholder: 'Main points the KOL should cover...' },
  {
    key: 'tone',
    label: 'Tone',
    type: 'select',
    options: [
      { value: 'friendly', label: 'Friendly & conversational' },
      { value: 'professional', label: 'Professional' },
      { value: 'humorous', label: 'Humorous' },
      { value: 'educational', label: 'Educational' },
      { value: 'inspirational', label: 'Inspirational' },
    ],
  },
  {
    key: 'platformFocus',
    label: 'Platform',
    type: 'select',
    options: [
      { value: 'instagram', label: 'Instagram' },
      { value: 'tiktok', label: 'TikTok' },
      { value: 'youtube', label: 'YouTube' },
    ],
  },
  {
    key: 'language',
    label: 'Language',
    type: 'select',
    options: [
      { value: 'English', label: 'English' },
      { value: 'Malay', label: 'Bahasa Melayu' },
      { value: 'Mandarin', label: 'Mandarin' },
    ],
    defaultValue: 'English',
  },
  { key: 'durationSeconds', label: 'Video duration (seconds)', type: 'number', placeholder: '60', defaultValue: '60' },
  { key: 'brandVoice', label: 'Brand voice', type: 'text', placeholder: 'e.g., Modern and youthful' },
  { key: 'avoidPhrases', label: 'Phrases to avoid', type: 'text', placeholder: 'e.g., cheap, discount' },
  { key: 'ctaPreference', label: 'Preferred call-to-action', type: 'text', placeholder: 'e.g., Visit our website, Use code X' },
];

function scriptsForInvitation(jobScripts: any[], invitationId: string) {
  return jobScripts
    .filter((s) => s.invitationId === invitationId)
    .sort((a, b) => (b.version || 0) - (a.version || 0));
}

export interface AgencyJobScriptsSummaryProps {
  jobId: string;
  jobTitle: string;
  invitations: Invitation[];
  jobScripts: any[];
  onRefresh: () => void | Promise<void>;
  /** Optional: show loading placeholder inside card */
  loading?: boolean;
}

/**
 * Shared script summary for a campaign: shortlisted / production-approved KOLs,
 * AI + manual draft, preview, and link to Script Studio (/agency/scripts/...).
 */
export default function AgencyJobScriptsSummary({
  jobId,
  jobTitle,
  invitations,
  jobScripts,
  onRefresh,
  loading = false,
}: AgencyJobScriptsSummaryProps) {
  const navigate = useNavigate();
  const [showScriptAI, setShowScriptAI] = useState(false);
  const [scriptAITarget, setScriptAITarget] = useState<{ invitationId: string; kolName: string } | null>(null);
  const [scriptAIGenerating, setScriptAIGenerating] = useState(false);
  const [scriptAIError, setScriptAIError] = useState<string | null>(null);
  const [scriptAIResult, setScriptAIResult] = useState<string | null>(null);
  const [scriptAISaving, setScriptAISaving] = useState(false);
  const [showManualScript, setShowManualScript] = useState(false);
  const [manualScriptTarget, setManualScriptTarget] = useState<{ invitationId: string; kolName: string } | null>(null);
  const [manualScriptContent, setManualScriptContent] = useState('');
  const [manualScriptSaving, setManualScriptSaving] = useState(false);

  const eligible = invitations.filter((inv) => inv.isApprovedForProduction || inv.isShortlisted);

  const handleScriptAIGenerate = async (values: Record<string, string>) => {
    if (!scriptAITarget) return;
    try {
      setScriptAIGenerating(true);
      setScriptAIError(null);
      setScriptAIResult(null);
      const res = await api.post('/ai/scripts/generate', {
        jobId,
        invitationId: scriptAITarget.invitationId,
        ...values,
        durationSeconds: values.durationSeconds ? parseInt(values.durationSeconds, 10) : 60,
      });
      setScriptAIResult(res.data.scriptText || JSON.stringify(res.data.generated, null, 2));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setScriptAIError(err.response?.data?.error || 'AI generation failed');
    } finally {
      setScriptAIGenerating(false);
    }
  };

  const handleScriptAIApply = async () => {
    if (!scriptAIResult || !scriptAITarget) return;
    try {
      setScriptAISaving(true);
      await api.post('/scripts', {
        jobId,
        invitationId: scriptAITarget.invitationId,
        content: scriptAIResult,
      });
      await onRefresh();
      setShowScriptAI(false);
      setScriptAIResult(null);
      setScriptAITarget(null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setScriptAIError(err.response?.data?.error || 'Failed to save script');
    } finally {
      setScriptAISaving(false);
    }
  };

  const handleManualScriptSave = async () => {
    if (!manualScriptTarget) return;
    const text = manualScriptContent.trim();
    if (!text) {
      alert('Enter script text before saving.');
      return;
    }
    try {
      setManualScriptSaving(true);
      await api.post('/scripts', {
        jobId,
        invitationId: manualScriptTarget.invitationId,
        content: text,
      });
      await onRefresh();
      setShowManualScript(false);
      setManualScriptTarget(null);
      setManualScriptContent('');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || 'Failed to save script');
    } finally {
      setManualScriptSaving(false);
    }
  };

  const openStudio = (scriptId: string) => {
    navigate(`/agency/scripts/${toSlug(jobTitle || 'script', scriptId)}`);
  };

  return (
    <>
      <Card>
        <CardContent className="space-y-6 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-glass-text">Script management</h3>
              <p className="text-sm text-glass-text-2">
                The brand must approve each KOL for production before you can create drafts with AI or manual save. Once a KOL
                has a script draft, the <strong className="font-medium text-glass-text">Open Script Studio</strong>{' '}
                button opens the full workspace (versions, brief, activity). That button appears on every row that already has a
                draft — even if shortlisting approval is still pending (you can review; editing may stay limited until approved).
              </p>
            </div>
          </div>

          {loading ? (
            <p className="py-8 text-center text-glass-text-3">Loading…</p>
          ) : eligible.length === 0 ? (
            <p className="py-8 text-center text-glass-text-3">No shortlisted KOLs yet. Shortlist KOLs on the Shortlist tab first.</p>
          ) : (
            <div className="divide-y divide-glass rounded-xl border border-glass">
              {eligible.map((inv) => {
                const kolScripts = scriptsForInvitation(jobScripts, inv.id);
                const latestScript = kolScripts[0];
                const canWriteScripts = inv.isApprovedForProduction;
                return (
                  <div key={inv.id} className="p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <span className="font-medium text-glass-text">
                          {inv.kolProfile?.name || 'Unknown KOL'}
                        </span>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          {inv.isApprovedForProduction ? (
                            <Badge variant="success" className="text-xs">
                              Shortlisting approved
                            </Badge>
                          ) : (
                            <Badge variant="warning" className="max-w-full whitespace-normal text-left text-xs">
                              Awaiting brand approval for shortlist (shortlisted only)
                            </Badge>
                          )}
                          {latestScript && (
                            <Badge
                              variant={
                                latestScript.status === 'APPROVED_BY_BRAND'
                                  ? 'success'
                                  : latestScript.status === 'REVISION_REQUESTED'
                                    ? 'destructive'
                                    : latestScript.status === 'SUBMITTED_FOR_BRAND'
                                      ? 'default'
                                      : 'secondary'
                              }
                              className="text-xs"
                            >
                              {latestScript.status === 'APPROVED_BY_BRAND'
                                ? 'Script approved'
                                : latestScript.status === 'REVISION_REQUESTED'
                                  ? 'Revision requested'
                                  : latestScript.status === 'SUBMITTED_FOR_BRAND'
                                    ? 'Under brand review'
                                    : `Script v${latestScript.version}`}
                            </Badge>
                          )}
                        </div>
                        {!canWriteScripts && (
                          <p className="mt-2 rounded-xl border border-amber-400/30 bg-amber-400/10 px-2 py-1.5 text-xs text-amber-500 dark:text-amber-300">
                            Script tools unlock after the brand approves shortlisting for this KOL.
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        {latestScript && (
                          <Button
                            type="button"
                            variant={canWriteScripts ? 'default' : 'outline'}
                            size="sm"
                            title={
                              canWriteScripts
                                ? 'Open full Script Studio for this KOL’s draft'
                                : 'Open Script Studio to view this draft (use AI / manual below only after brand approves production)'
                            }
                            onClick={() => openStudio(latestScript.id)}
                          >
                            Open Script Studio
                          </Button>
                        )}
                        {kolScripts.length > 0 && (
                          <span className="text-xs text-glass-text-3">
                            {kolScripts.length} version{kolScripts.length !== 1 ? 's' : ''}
                          </span>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!canWriteScripts}
                          title={canWriteScripts ? 'Generate a draft with AI' : 'Requires production approval from brand'}
                          onClick={() => {
                            setScriptAITarget({ invitationId: inv.id, kolName: inv.kolProfile?.name || 'KOL' });
                            setScriptAIResult(null);
                            setScriptAIError(null);
                            setShowScriptAI(true);
                          }}
                          className="flex items-center gap-1"
                        >
                          <Sparkles className="h-3 w-3" />
                          AI draft
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!canWriteScripts}
                          title={canWriteScripts ? 'Type or paste a script and save as draft' : 'Requires production approval from brand'}
                          onClick={() => {
                            setManualScriptTarget({ invitationId: inv.id, kolName: inv.kolProfile?.name || 'KOL' });
                            setManualScriptContent(latestScript?.content || '');
                            setShowManualScript(true);
                          }}
                          className="flex items-center gap-1"
                        >
                          <PenLine className="h-3 w-3" />
                          Manual draft
                        </Button>
                      </div>
                    </div>
                    {latestScript && (
                      <div className="mt-3 rounded-xl border border-glass bg-glass-2 p-3">
                        <p className="line-clamp-3 whitespace-pre-wrap text-sm text-glass-text-2">
                          {latestScript.content?.trim()
                            ? latestScript.content
                            : 'Open Script Studio to view the full script.'}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {showScriptAI && (
        <AIAssistantShell
          open={showScriptAI}
          onClose={() => {
            setShowScriptAI(false);
            setScriptAITarget(null);
            setScriptAIResult(null);
          }}
          title={`Generate Script for ${scriptAITarget?.kolName || 'KOL'}`}
          description="Provide details to generate an AI-powered script draft"
          fields={scriptAIFields}
          onGenerate={handleScriptAIGenerate}
          generating={scriptAIGenerating}
          error={scriptAIError}
          result={
            scriptAIResult ? (
              <pre className="whitespace-pre-wrap text-sm text-glass-text">{scriptAIResult}</pre>
            ) : undefined
          }
          applyLabel="Save as Draft"
          onApply={handleScriptAIApply}
          applying={scriptAISaving}
        />
      )}

      <Modal
        isOpen={showManualScript && !!manualScriptTarget}
        onClose={() => {
          setShowManualScript(false);
          setManualScriptTarget(null);
          setManualScriptContent('');
        }}
        title={manualScriptTarget ? `Manual script — ${manualScriptTarget.kolName}` : 'Manual script'}
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowManualScript(false);
                setManualScriptTarget(null);
                setManualScriptContent('');
              }}
              disabled={manualScriptSaving}
            >
              Cancel
            </Button>
            <Button type="button" onClick={() => void handleManualScriptSave()} disabled={manualScriptSaving}>
              {manualScriptSaving ? 'Saving…' : 'Save as draft'}
            </Button>
          </div>
        }
      >
        <p className="mb-3 text-sm text-glass-text-2">
          Paste or write the full script. This creates a new draft version (same as AI save).
        </p>
        <Textarea
          rows={16}
          className="w-full font-mono text-sm"
          value={manualScriptContent}
          onChange={(e) => setManualScriptContent(e.target.value)}
          placeholder="Hook, talking points, CTA, captions…"
        />
      </Modal>
    </>
  );
}
